import { useCallback, useContext, useEffect, useMemo, useState, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useErrorCorrectionContext } from 'error-correction'
import { motion } from 'framer-motion'
import { isEmpty } from 'lodash'
import { bool, number, oneOfType, string } from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { useDeepCompareEffect } from 'react-use'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { PendingStatus, DatasetStatus } from '@components/dataset'
import { GridFooter } from '@components/grid-footer'
import { submissionContext, useSearchParamsContext, templateContext } from '@contexts'
import { useDatasetQueryContext } from '@datasets/dataset-query-context/hooks'
import { ActionType as DatasetReducerType } from '@datasets/dataset-query-context/types/context'
import { ReadGridToolbarProvider } from '@datasets/handsontable/read-grid/context/toolbar'
import { useDatasetRemovedRowsObserver } from '@global/hooks/queries/useDatasetRemovedRowsObserver'
import {
  useDatasetsDataQueryObserver,
  KEY,
} from '@global/hooks/queries/useDatasetsDataQueryObserver'
import { showSnackbar } from '@global/snackbar/snackbar.actions'
import { BulkUploadModal } from '@submissions/templates/bulk-upload-modal'
import { calculateOffset, padArray } from '@utils/datasetQueryUtils'
import { api } from 'api'
import { UrlAudits } from 'handsontable/utils/codes.url-audits'
import { BulkResubmitModal } from './bulk-resubmit-modal'
import { useDatasetDataContext } from './datasetData/context'
import { ActionType } from './datasetData/context/types'
import { Header } from './header'
import { fetchIsSubmissionOutdated } from './query'
import { getDatasetValues, buildDatasetStructure } from './utils'
import { DatasetBanner, VariantTypes } from '../components/dataset-banner'
import * as DatasetActions from '../datasets.actions'
import { DiffProvider } from '../handsontable/read-grid/context/diff-context'
import { ReadGrid } from '../handsontable/read-grid/read-grid'
import { t } from '@lingui/core/macro'
import { trackEvent, EventTrackingEventType } from 'lib/eventTracking'

const QUERY_LIMIT = 10000
const QUERY_OFFSET = 0

const HEADER_HEIGHT = 85

/**
 * Dataset management.
 * @param {*} props React component props.
 * @param {string} props.templateId React component props.
 * @param {string} props.id React component props.
 * @param {boolean} props.metaLoading Disables the read grid until metadata loads
 * @returns ReactElement
 */
export const DatasetData = ({ id, templateId = '', metaLoading = false }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const {
    datasetLoading,
    datasetRowCount,
    limit,
    offset,
    queryRowCountIsLoading,
    queryRowCount,
    setOffsetDebounced,
    reducerDispatch: datasetQueryContextReducerDispatch,
  } = useDatasetQueryContext()
  const { correctErrors } = useErrorCorrectionContext()
  const { resetSubmissionByDatasetId } = useContext(submissionContext)
  const { byId: templatesById } = useContext(templateContext)
  const { params, updateCache } = useSearchParamsContext()
  const {
    bulkUploadModalIsOpen,
    bulkResubmitModalIsOpen,
    selectedRows,
    selectiveEditModeEnabled,
    datasetTemplateId,
    removingRows,
    dispatch: dispatchState,
  } = useDatasetDataContext()
  const selectiveEditModeEnabledRef = useRef(selectiveEditModeEnabled)
  const [DATASETS_ENABLED = false] = useSelector(selectFeatureIncludes([FeatureDict.DATASETS_PAGE]))

  const meta = useSelector(state => state.Datasets.datasetMetadataById[id])
  const [template, setTemplate] = useState(null)
  const { data } = useDatasetsDataQueryObserver({ datasetId: id })
  const isViewingSingleSubmission = location.pathname.includes('/codes/details/')
  const queryClient = useQueryClient()

  const { data: isSubmissionOutdated, isInitialLoading: isLoadingIfSubmissionIsOutdated } =
    useQuery(['isSubmissionOutdated', id], () => fetchIsSubmissionOutdated(id), {
      refetchOnWindowFocus: false,
      enabled: isViewingSingleSubmission,
    })

  const { addRemovedRows } = useDatasetRemovedRowsObserver({ datasetId: id })

  const requireApproval = Boolean(
    meta?.latest_submission_status === PendingStatus.AWAITING_APPROVAL ||
      meta?.latest_submission_status === PendingStatus.REJECTED,
  )

  // Necessary for DatasetDataContext listeners to receive id for datasetMetaById
  useEffect(() => {
    if (!id) return
    dispatchState({ type: ActionType.setDatasetDataId, payload: id })
  }, [id])

  useDeepCompareEffect(
    () => () => {
      delete params.search
      delete params.filter
      delete params.sort
      updateCache(params)
    },
    [params],
  )

  // Set the template ID in the dataset context;
  // I don't like syncing up the templateID into context; feels like a hack;
  // but it's necessary for other components relying on context to get this filled.
  useEffect(() => {
    if (templateId === datasetTemplateId) return
    dispatchState({ type: 'datasetTemplateId', payload: { id: templateId } })
  }, [templateId, datasetTemplateId])

  // Fetch template if not found in the template context.
  useEffect(() => {
    if (!templateId) return

    if (!isEmpty(templatesById[templateId])) {
      /*
        There are some cases where the template state is null but the template is in the template context.
        In those cases, we don't need to load the template again, we can just set it from the context.  
      */
      if (!template) setTemplate(templatesById[templateId])

      return
    }

    loadTemplate()

    async function loadTemplate() {
      const accountTemplateUrl = `/api/v1/account/template/${templateId}`

      const { data: templateData } = await api.get(accountTemplateUrl)
      setTemplate(templateData)
    }
  }, [templateId])

  useEffect(() => {
    UrlAudits.set([])
    UrlAudits.clearQueue()

    dispatch(DatasetActions.getTemplateDependencies(templateId, templatesById[templateId] || {}))
  }, [])

  const rows = useMemo(
    () => (!isEmpty(data) ? padArray(data.rows, offset, queryRowCount) : []),
    [data?.rows, offset, queryRowCount, template?.creativeConfiguration],
  )

  const columns = (() => {
    if (isEmpty(data)) {
      return []
    }

    if (data?.filteredColumns) {
      return data.filteredColumns
    }

    return data.columns
  })()
  /*
    Correct the indices. When selecting rows in ReadGrid, the indices are incremented
    by 1 to ignore the header row.
  */
  const selectedRowIndices = Object.keys(selectedRows).map(idx => idx - 1)

  // Reset selected rows when selectiveEditModeEnabled is set to true the first time.
  useEffect(() => {
    if (!selectiveEditModeEnabled) return
    if (selectiveEditModeEnabled === selectiveEditModeEnabledRef.current) return

    dispatchState({ type: 'selectedRows', payload: { data: {} } })
  }, [selectiveEditModeEnabled, selectiveEditModeEnabledRef.current])

  const reload = (datasetId, shouldReloadPage) => {
    if (shouldReloadPage) {
      history.push(`/codes/details/${datasetId}/data/processing`)
    }
    resetSubmissionByDatasetId(datasetId)
    dispatchState({ type: 'editMode', payload: { isEnabled: false } })
  }

  const removeRows = useCallback(async () => {
    const cvIds = Object.keys(selectedRows).map(i => selectedRows[i].cvid)
    try {
      dispatchState({ type: 'removeRows', payload: { removingRows: true } })
      await api.post(`/api/v1/account/dataset/${id}/data/removeRows`, { cvIds })
      addRemovedRows(cvIds)
      dispatchState({ type: 'removeRows', payload: { removingRows: false } })

      const currentData = queryClient.getQueryData([KEY]) ?? {}
      const updatedData = {
        ...currentData,
        [id]: {
          ...data,
          rows: data.rows.filter(({ cvid }) => !cvIds.includes(cvid)),
        },
      }

      datasetQueryContextReducerDispatch({
        type: DatasetReducerType.setQueryRowCount,
        payload: queryRowCount - cvIds.length,
      })

      dispatchState({ type: 'selectedRows', payload: { data: {} } })

      queryClient.setQueryData([KEY], updatedData)
      dispatch(
        showSnackbar({
          message: t`Successfully initiated row removal request`,
          type: 'SUCCESS',
          duration: 5000,
        }),
      )
    } catch (_e) {
      dispatchState({ type: 'removeRows', payload: { removingRows: false } })
      dispatch(
        showSnackbar({
          message: t`Failed to remove rows`,
          type: 'ERROR',
          duration: 5000,
        }),
      )
    }
  }, [selectedRows, dispatchState, dispatch, datasetQueryContextReducerDispatch, queryRowCount])

  const isAutopopulatedDate = fieldId => {
    const templateField = template.template_fields.find(field => field.field_id === fieldId)
    return !!templateField.field.date.auto_populate
  }

  const duplicateRows = useCallback(async () => {
    let templateFields = template?.template_fields
    try {
      if (!templateFields) {
        // Template is lost in some cases, so we need to fetch it again.
        // It's not clear why this state is lost.
        const { data: templateData } = await api.get(`/api/v1/account/template/${templateId}`)
        templateFields = templateData.template_fields
      }
    } catch (error) {
      dispatch(
        showSnackbar({
          message: `${t`Failed to duplicate rows. There was an error fetching template fields.`} \n ${error}`,
          type: 'ERROR',
          duration: 5000,
        }),
      )
    }

    try {
      const filteredColumns = data.columns.filter(column => {
        if (column.fieldId === 0) return false

        switch (column.governanceType) {
          case 'text':
          case 'list':
          case null:
            return true
          case 'date':
            return !isAutopopulatedDate(column.fieldId)
          default:
            return false
        }
      })

      const duplicateRowsDatasetValues = selectedRowIndices.map(idx => ({
        dataset_values: getDatasetValues({
          idx: idx + 1, // indices are indexes of rows in the grid, which are off the actual Row ID by 1.
          filteredColumns,
          rows: selectedRows,
          templateFields,
        }),
      }))

      dispatch(DatasetActions.setDatasetDuplicatedRows(duplicateRowsDatasetValues))
      history.push(`/codes/create/${templateId}`)

      trackEvent(EventTrackingEventType.ROWS_DUPLICATED, {
        rowCount: selectedRowIndices.length,
      })
    } catch (_error) {
      dispatch(
        showSnackbar({
          message: t`Failed to duplicate rows. Please resolve template configuration errors.`,
          type: 'ERROR',
          duration: 5000,
        }),
      )
    }
  }, [
    data?.columns,
    selectedRowIndices,
    templateId,
    dispatch,
    data?.rows,
    template?.template_fields,
  ])

  const manualEditRows = async () => {
    dispatchState({ type: 'setManualEditLoading', payload: { loading: true } })
    const canSubmitAllData = await api.post(`/api/core/v1/datasets/${id}/query`, {
      limit: QUERY_LIMIT,
      offset: QUERY_OFFSET,
      orderBy: [],
      where: {
        operator: '_and',
        value: [
          { operator: '_errors' },
          {
            column: 'cv_id',
            operator: '_nin',
            value: Object.keys(selectedRows).map(i => selectedRows[i].cvid),
          },
        ],
      },
    })
    const canSubmitAll = canSubmitAllData.data.queryRowCount === 0
    const creativeClassificationColumns = columns.filter(
      ({ isClassificationColumn }) => isClassificationColumn,
    )
    const datasetStructuresByDatasetId = {
      [id]: buildDatasetStructure(meta, selectedRows, rows, creativeClassificationColumns),
    }

    dispatchState({
      type: 'manualEditRows',
      payload: { canSubmitAll, datasetStructuresByDatasetId },
    })
    dispatchState({ type: ActionType.editMode, payload: { isEnabled: false } })
    history.push('selective')
  }

  const correctErroredRows = async () => {
    try {
      const correctedRows = await correctErrors(id, selectedRows)
      const creativeClassificationColumns = columns.filter(
        ({ isClassificationColumn }) => isClassificationColumn,
      )

      const datasetStructuresByDatasetId = {
        [id]: buildDatasetStructure(
          meta,
          selectedRows,
          rows,
          creativeClassificationColumns,
          correctedRows?.rows,
        ),
      }
      dispatchState({
        type: 'manualEditRows',
        payload: { canSubmitAll: false, datasetStructuresByDatasetId },
      })
      dispatchState({ type: ActionType.editMode, payload: { isEnabled: false } })
      history.push('selective')
    } catch (_error) {
      dispatch(
        showSnackbar({
          message: t`Failed to correct errored rows`,
          type: 'ERROR',
          duration: 5000,
        }),
      )
    }
  }

  const loading = datasetLoading || metaLoading || queryRowCountIsLoading

  const handleSectionRendered = useCallback(
    ({ rowStartIndex, rowStopIndex }) => {
      const dataStartIndex = offset
      const dataStopIndex = offset + limit

      if (!loading && (rowStartIndex < dataStartIndex || rowStopIndex > dataStopIndex)) {
        const newOffset = calculateOffset(rowStartIndex, rowStopIndex, queryRowCount, limit)
        setOffsetDebounced(newOffset)
      }
    },
    [loading, limit, offset, queryRowCount, setOffsetDebounced],
  )

  const isSubmissionUpToDate = !isSubmissionOutdated
  const isSubmissionSuccessful = meta?.latest_submission_status === DatasetStatus.COMPLETE
  const shouldShowDatasetBanner =
    DATASETS_ENABLED &&
    isViewingSingleSubmission &&
    isSubmissionSuccessful &&
    !isLoadingIfSubmissionIsOutdated
  const datasetBannerVariantOption = isSubmissionUpToDate ? VariantTypes.INFO : VariantTypes.WARNING

  return (
    <>
      <motion.div
        className="DatasetData"
        style={{ height: 'calc(100% - 60px)' }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, transition: { duration: 0.5 } }}
        exit={{ opacity: 0, transition: { duration: 0.5 } }}
      >
        <ReadGridToolbarProvider>
          <DiffProvider>
            <Header
              datasetId={id}
              templateId={datasetTemplateId}
              selectedRowIndices={selectedRowIndices}
              manualEditRows={manualEditRows}
              correctErroredRows={correctErroredRows}
              removeRows={removeRows}
              removingRows={removingRows}
              duplicateRows={duplicateRows}
              datasetRowCount={queryRowCount}
              shouldShowEditConfirmModal={shouldShowDatasetBanner && !isSubmissionUpToDate}
              columns={columns}
              rows={rows}
            />
            {shouldShowDatasetBanner && (
              <DatasetBanner datasetId={datasetTemplateId} variant={datasetBannerVariantOption} />
            )}
            <div style={{ height: `calc(100% - ${HEADER_HEIGHT}px)` }}>
              <ReadGrid
                id={id}
                data-test-id="read-grid"
                key={meta && meta.id}
                columns={columns}
                limit={limit}
                loading={loading}
                meta={meta}
                onSectionRendered={handleSectionRendered}
                rowCount={queryRowCount}
                rows={rows}
                isFiltered={data?.filteredColumns}
              />
            </div>
          </DiffProvider>
        </ReadGridToolbarProvider>
        <BulkUploadModal
          open={bulkUploadModalIsOpen}
          setOpen={setBulkUploadModalIsOpen}
          templateId={datasetTemplateId}
          requireApproval={requireApproval}
        />
        <BulkResubmitModal
          onProgressClick={reload}
          open={bulkResubmitModalIsOpen}
          datasetId={id}
          setOpen={setBulkResubmitModalIsOpen}
        />
      </motion.div>
      <GridFooter
        datasetId={id}
        displayedRowsCount={rows.length}
        allRowsCount={datasetRowCount}
        selectedRows={Object.keys(selectedRows)}
      />
    </>
  )

  function setBulkUploadModalIsOpen(isOpen) {
    dispatchState({ type: ActionType.setModal, payload: { bulkUploadOpen: isOpen } })
  }

  function setBulkResubmitModalIsOpen(isOpen) {
    dispatchState({ type: ActionType.setModal, payload: { resubmitOpen: isOpen } })
  }
}

DatasetData.propTypes = {
  id: oneOfType([string, number]).isRequired,
  templateId: oneOfType([string, number]),
  metaLoading: bool,
}
